if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        // this.init();
        FoxTheme.Motion.inView(this, this.init.bind(this));

        this._onPurchaseOption = this.onPurchaseOptionChange.bind(this);
        document.addEventListener('purchase-option:change', this._onPurchaseOption);
      }

      disconnectedCallback() {
        document.removeEventListener('purchase-option:change', this._onPurchaseOption);
        clearInterval(this._widgetPollTimer);
        clearInterval(this._widgetSyncTimer);
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';

        // Capture the full media set once, then filter to the initially selected
        // colour on first load. Products that don't use the "Color: <name>;" alt
        // convention keep the default (unfiltered) behaviour.
        // Media tagged "Purchase: subscription|onetime;" is also filtered by the purchase
        // choice: the Recharge widget on the PDP, or the <purchase-options> toggle in Quick View
        // (its retained state covers a toggle that emitted before we init).
        if (!this.masterMediaItems) {
          this.masterMediaItems = [...this.elements.mediaList.querySelectorAll('.product__media-item')];
          this.hasColorMedia = this.masterMediaItems.some((m) => m.dataset.mediaColor);
          this.hasPurchaseMedia = this.masterMediaItems.some((m) => m.dataset.mediaPurchase);
          this.currentColors = this.dataset.selectedColor ? [this.dataset.selectedColor] : [];
          if (this.purchaseType === undefined) {
            const widget = this.purchaseWidget;
            const retained = (window.__purchaseOptionState || {})[this.dataset.productId];
            this.purchaseType =
              (widget && this.readWidgetType(widget)) ||
              (retained ? (retained.isSubscription ? 'subscription' : 'onetime') : '');
          }
          if ((this.hasColorMedia && this.currentColors.length) || (this.hasPurchaseMedia && this.purchaseType)) {
            this.rebuildMediaList(this.currentColors);
          }
        }

        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.hasPurchaseMedia && !this._widgetBinding) {
          this._widgetBinding = true;
          this.bindPurchaseWidget();
        }

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: this.dataset.paginationType === 'dots' ? 'bullets' : 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items = []) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        // Products using the "Color: <name>;" alt convention: filter the gallery
        // to the selected colour's media (plus any shared/untagged media).
        if (this.hasColorMedia && variant && Array.isArray(variant.options)) {
          this.currentColors = variant.options;
          this.applyVariantFilter(variant.options);
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideTo(0, 0, false);
          }
          return;
        }

        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant);
        }
      }

      // <purchase-options> toggle (Quick View). Ignored once a Recharge widget drives this gallery.
      onPurchaseOptionChange(e) {
        if (!e.detail || String(e.detail.productId) !== String(this.dataset.productId)) return;
        if (this._widgetBound) return;
        this.setPurchaseType(e.detail.isSubscription ? 'subscription' : 'onetime');
      }

      /** The Recharge widget in this gallery's own product-info (PDP); none in Quick View. */
      get purchaseWidget() {
        const scope = this.closest('product-info') || document;
        return scope.querySelector(`recharge-subscription-widget[product-id="${this.dataset.productId}"]`);
      }

      /** 'subscription' | 'onetime', or null while the widget hasn't settled. */
      readWidgetType(widget) {
        const option = widget.purchaseOption;
        if (option === 'onetime') return 'onetime';
        if (typeof option === 'string' && option.length) return 'subscription';
        return widget.sellingPlanId ? 'subscription' : null;
      }

      // The Recharge widget upgrades async and emits no change event we can rely on, so (like
      // subscription-price.js) re-read it on click/change plus a lightweight persistent poll.
      bindPurchaseWidget() {
        const attach = () => {
          const widget = this.purchaseWidget;
          if (!widget) return false;
          this._widgetBound = true;
          const sync = () => {
            const type = this.readWidgetType(widget);
            if (type) this.setPurchaseType(type);
          };
          widget.addEventListener('click', () => setTimeout(sync, 0));
          widget.addEventListener('change', () => setTimeout(sync, 0));
          this._widgetSyncTimer = setInterval(sync, 400);
          sync();
          return true;
        };
        if (attach()) return;
        let tries = 0;
        this._widgetPollTimer = setInterval(() => {
          if (attach() || tries++ > 40) clearInterval(this._widgetPollTimer);
        }, 250);
      }

      // Re-filter to the chosen purchase type's media and go back to the first slide.
      // Frequency-only changes (same type) are ignored.
      setPurchaseType(type) {
        if (type === this.purchaseType) return;
        this.purchaseType = type;
        // Not initialised yet: init() picks up this.purchaseType.
        if (!this.masterMediaItems || !this.hasPurchaseMedia) return;

        this.applyVariantFilter(this.currentColors);
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.slideTo(0, 0, false);
        }
      }

      // Rebuilds the media list to contain only the media matching the given
      // colour values (matched against data-media-color) and the current purchase
      // type (data-media-purchase), keeping untagged (shared) media. Falls back to
      // the full set if nothing matches.
      rebuildMediaList(colorValues) {
        if (!this.masterMediaItems || !this.masterMediaItems.length) return;

        const colors = (colorValues || []).map((c) => String(c).toLowerCase()).filter(Boolean);
        const purchase = this.purchaseType || '';
        let visible = this.masterMediaItems;

        if (colors.length || purchase) {
          visible = this.masterMediaItems.filter((media) => {
            const mediaColor = (media.dataset.mediaColor || '').toLowerCase();
            const mediaPurchase = (media.dataset.mediaPurchase || '').toLowerCase();
            const colorMatch = !colors.length || mediaColor === '' || colors.includes(mediaColor);
            const purchaseMatch = !purchase || mediaPurchase === '' || mediaPurchase === purchase;
            return colorMatch && purchaseMatch;
          });
          if (!visible.length) visible = this.masterMediaItems;
        }

        this.elements.mediaList.innerHTML = '';
        visible.forEach((media) => this.elements.mediaList.appendChild(media));
        this.elements.mediaItems = visible;
      }

      // Filter + reinitialise the slider/zoom for the new visible set.
      applyVariantFilter(colorValues) {
        this.rebuildMediaList(colorValues);
        this.destroySlider();
        this.updateMediaLayout();
        if (this.enableImageZoom) {
          this.initImageZoom(this.elements.mediaItems);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        this.initImageZoom(newMedias);

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
